import { useMemo } from "react";
import {
  useGetExistingAccountPaymentPrefsQuery,
  useGetSupportedPaymentMethodsForAccountPositionsQuery,
} from "../../../../graphql/supergraph.index";
import { PaymentMethodEnum } from "../../../../graphql/supergraphOperations";
import { CreatePaymentPrefEntryPoint } from "../../common/enums";
import { SelectAvailablePaymentPrefsForm } from "../../Components/SelectAvailablePaymentPrefsForm";
import { type ChildComponentProps } from "../../Components/types";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { getAvailablePaymentMethods } from "../../mappers";
import { type SupportedPaymentMethodAccount } from "./types";

/**
 * Reduces the supported payment methods for all the positions of an account into
 * a single array of the common supported methods for each position.
 *
 * The check inspects the position's investment entity's supported payment methods.
 */
const reduceSupportedPaymentMethods = (account?: SupportedPaymentMethodAccount) => {
  let commonPaymentMethods = [
    PaymentMethodEnum.Ach,
    PaymentMethodEnum.Check,
    PaymentMethodEnum.Wire,
  ];

  if (!account?.positions?.edges) {
    return [];
  }

  for (const position of account.positions.edges) {
    const supported = position.node.investmentEntity.supportedPaymentMethods;
    commonPaymentMethods = commonPaymentMethods.filter((method) => supported.includes(method));
  }

  return commonPaymentMethods;
};

export const usePaymentPrefCreationForAccountWorkflowController = ({
  accountId,
  sourcePaymentPrefId,
}: {
  accountId: string;
  sourcePaymentPrefId?: string;
}) => {
  /**
   * There are two possible entry points for the create workflow. See enum definition for more details
   */
  const accountCreateEntryPointType =
    sourcePaymentPrefId && sourcePaymentPrefId.length > 0
      ? CreatePaymentPrefEntryPoint.Switch
      : CreatePaymentPrefEntryPoint.Add;

  /**
   * Fetch the data required to determined supported payment methods
   */
  const {
    data: supportedPaymentMethodsData,
    loading: supportedPaymentMethodsLoading,
    error: supportedPaymentMethodsError,
  } = useGetSupportedPaymentMethodsForAccountPositionsQuery({
    variables: {
      accountId,

      /**
       * We only need to check the payment method support for
       * the positions we are intending to update
       */
      filters: {
        ...(accountCreateEntryPointType === CreatePaymentPrefEntryPoint.Add
          ? {
              /**
               * When we are coming from the 'add' entry point, we are applying
               * a payment pref to positions that do not have a payment pref
               */
              hasPaymentPref: false,
            }
          : {
              /**
               * When are coming from the 'switch' entry point, we are
               * appling a payment pref to positions that have a specific
               * existing payment pref, identified by the prop sourcePaymentPrefId
               */
              paymentPrefId: sourcePaymentPrefId,
            }),
      },
    },
  });

  /**
   * Common supported payment methods for account positions
   * Mapped into the shape that the context expects
   */
  const availablePaymentMethods = useMemo(() => {
    const reducedPaymentMethods = reduceSupportedPaymentMethods(
      supportedPaymentMethodsData?.account,
    );

    // format in the shape we expect
    return getAvailablePaymentMethods({
      availablePaymentMethods: reducedPaymentMethods,
    });
  }, [supportedPaymentMethodsData?.account]);

  /**
   * Fetch the existing payment prefs on account
   * Used to display the available payment prefs,
   * after filtering by supported payments.
   */
  const {
    data: existingAccountPaymentPrefsData,
    loading: existingAccountPaymentPrefsLoading,
    error: existingAccountPaymentPrefsError,
  } = useGetExistingAccountPaymentPrefsQuery({
    variables: {
      accountId,
    },
  });

  /**
   * The prefix component passed into the base workflow
   *
   * If there are existing payment prefs, display the select
   * available payment prefs component, otherwise there is no prefix.
   *
   * If there is only existing payment pref and we're switching from it,
   * then there's no need to display the select available payment prefs component.
   * See https://junipersquare.atlassian.net/browse/FF-3344 for more details.
   */
  const prefixComponents = useMemo(() => {
    let prefix: React.FC<ChildComponentProps>[] = [];

    if (
      existingAccountPaymentPrefsData?.account?.paymentPrefs &&
      existingAccountPaymentPrefsData?.account?.paymentPrefs.length >
        (accountCreateEntryPointType === CreatePaymentPrefEntryPoint.Switch ? 1 : 0)
    ) {
      prefix = [SelectAvailablePaymentPrefsForm];
    }
    return prefix;
  }, [accountCreateEntryPointType, existingAccountPaymentPrefsData?.account?.paymentPrefs]);

  return {
    /**
     * Combining all the loading states into one
     */
    dataLoading: supportedPaymentMethodsLoading || existingAccountPaymentPrefsLoading,

    /**
     * Combining all the error states into one
     */
    dataError: supportedPaymentMethodsError || existingAccountPaymentPrefsError,

    existingAccountPaymentPrefsData,
    availablePaymentMethods,
    prefixComponents,

    /**
     * The type of payment pref association mutation to apply the prefs.
     * If it is a switch entry point, we want to update all the positions with the same pref
     * Otherwise if it is an add payment pref
     */
    associatePaymentPrefType:
      accountCreateEntryPointType === CreatePaymentPrefEntryPoint.Switch
        ? AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
        : AssociatePaymentPrefMutationTypeEnum.PositionsWithoutPaymentPrefOfAccount,

    /**
     * If we are not coming from the switch entry point, want to explicitly make
     * the payment pref id undefined.
     */
    paymentPrefIdForContext:
      accountCreateEntryPointType === CreatePaymentPrefEntryPoint.Switch
        ? sourcePaymentPrefId
        : undefined,
  };
};
